import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useState } from "react";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Eye, CheckCircle, XCircle, FileText, ExternalLink, Image } from "lucide-react";

export const KYCSection = ({ kycVerifications, fetchData }) => {
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleReview = async (status) => {
    if (!selectedKyc) return;
    if (status === "rejected" && !rejectionReason) {
      toast.error("Please provide a rejection reason");
      return;
    }
    try {
      await axios.put(`${API}/admin/kyc-verifications/${selectedKyc.kyc_id}`, {
        status, rejection_reason: status === "rejected" ? rejectionReason : null
      }, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(`KYC ${status}`);
      setReviewDialogOpen(false);
      setSelectedKyc(null);
      setRejectionReason("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to review KYC");
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending: "bg-amber-100 text-amber-700",
      under_review: "bg-blue-100 text-blue-700",
      approved: "bg-emerald-100 text-emerald-700",
      rejected: "bg-rose-100 text-rose-700",
    };
    return map[status] || map.pending;
  };

  const isImageUrl = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.endsWith(".jpg") || lower.endsWith(".jpeg") || lower.endsWith(".png") || lower.endsWith(".gif") || lower.endsWith(".webp");
  };

  const formatDocType = (type) => {
    return (type || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <>
      <Card className="bg-white border-slate-200">
        <CardHeader className="border-b border-slate-100">
          <CardTitle className="text-slate-800 font-heading flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" /> Identity Verifications
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {kycVerifications.length === 0 ? (
            <div className="p-8 text-center text-slate-500">No verification requests yet</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-slate-500 text-xs uppercase tracking-wider bg-slate-50">
                    <th className="px-5 py-3 font-semibold">User</th>
                    <th className="px-5 py-3 font-semibold">Documents</th>
                    <th className="px-5 py-3 font-semibold">Status</th>
                    <th className="px-5 py-3 font-semibold">Submitted</th>
                    <th className="px-5 py-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {kycVerifications.map((kyc) => (
                    <tr key={kyc.kyc_id} className="hover:bg-slate-50">
                      <td className="px-5 py-3">
                        <p className="text-slate-700 font-medium text-sm">{kyc.user_name}</p>
                        <p className="text-slate-500 text-xs">{kyc.user_email}</p>
                        <p className="text-xs text-slate-400">Tier {kyc.current_tier}</p>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap gap-1">
                          {kyc.documents?.map((doc, i) => (
                            <a
                              key={i}
                              href={doc.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs hover:bg-blue-100 transition-colors cursor-pointer"
                              data-testid={`view-doc-${kyc.kyc_id}-${i}`}
                            >
                              {isImageUrl(doc.file_url) ? <Image className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                              {formatDocType(doc.document_type)}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ))}
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusBadge(kyc.status)}`}>{kyc.status.replace("_", " ")}</span>
                      </td>
                      <td className="px-5 py-3 text-slate-500 text-sm">{new Date(kyc.submitted_at).toLocaleDateString()}</td>
                      <td className="px-5 py-3">
                        <Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1" onClick={() => { setSelectedKyc(kyc); setRejectionReason(""); setReviewDialogOpen(true); }} data-testid={`review-kyc-${kyc.kyc_id}`}>
                          <Eye className="w-3.5 h-3.5" /> Review
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-slate-800 font-heading">Review KYC Verification</DialogTitle></DialogHeader>
          {selectedKyc && (
            <div className="space-y-5 pt-4">
              {/* Applicant Info */}
              <div className="bg-slate-50 p-4 rounded-xl">
                <p className="text-sm text-slate-500">Applicant</p>
                <p className="text-slate-700 font-medium">{selectedKyc.user_name}</p>
                <p className="text-slate-500 text-sm">{selectedKyc.user_email}</p>
                <p className="text-xs text-slate-400 mt-1">Current Status: <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${getStatusBadge(selectedKyc.status)}`}>{selectedKyc.status.replace("_", " ")}</span></p>
              </div>

              {/* Documents Grid */}
              <div>
                <p className="text-sm font-medium text-slate-700 mb-3">Submitted Documents ({selectedKyc.documents?.length || 0})</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {selectedKyc.documents?.map((doc, i) => (
                    <div key={i} className="rounded-xl border border-slate-200 overflow-hidden bg-white">
                      <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200">
                        <span className="text-slate-700 text-sm font-medium flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-500" />
                          {formatDocType(doc.document_type)}
                        </span>
                        <a href={doc.file_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors"
                          data-testid={`open-doc-${i}`}>
                          <ExternalLink className="w-3 h-3" /> Open
                        </a>
                      </div>
                      {isImageUrl(doc.file_url) ? (
                        <div className="p-2 bg-white">
                          <img src={doc.file_url} alt={doc.document_type} className="w-full h-48 object-contain rounded" />
                        </div>
                      ) : (
                        <div className="p-4 text-center">
                          <FileText className="w-10 h-10 text-slate-400 mx-auto mb-1" />
                          <p className="text-xs text-slate-500">PDF Document</p>
                          <p className="text-[10px] text-slate-400 font-mono break-all mt-1">{doc.file_url}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Review Actions */}
              <div className="space-y-3 pt-2 border-t border-slate-200">
                <div className="space-y-2">
                  <Label>Rejection Reason (required if rejecting)</Label>
                  <Input placeholder="e.g., Document is blurry, expired, or doesn't match..." value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} data-testid="rejection-reason-input" />
                </div>
                <div className="flex gap-3">
                  <Button className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white" onClick={() => handleReview("approved")} data-testid="approve-kyc-btn">
                    <CheckCircle className="w-4 h-4 mr-2" /> Approve & Verify User
                  </Button>
                  <Button variant="outline" className="flex-1 border-rose-300 text-rose-600 hover:bg-rose-50" onClick={() => handleReview("rejected")} data-testid="reject-kyc-btn">
                    <XCircle className="w-4 h-4 mr-2" /> Reject
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
